import { type CSSProperties } from "react";
import { NotchPanel, NotchedInput, KeycapButton } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Verification harness for NotchPanel — the general flat notched surface.
 * Verify in a real browser (shape() clip): the hairline keyline traces the
 * notch, the .elev-* drop-shadow survives the clip (no slice at the notch), and
 * the panel hosts the other notched primitives as a coherent card.
 */
export function NotchPanelDemo() {
  return (
    <Section
      id="notch-panel"
      title="NotchPanel"
      description="The general-purpose flat surface in the hex-notch silhouette — the base container of the notch design language (distinct from artifact-specific ArtifactTile, frosted-floating GlassSurface, and the rounded Card family). Elevation is a clip-safe .elev-* drop-shadow; the keyline is a continuous nx-rim ring (a real border is sliced at the notch). notch t…l (default m), elev none/soft/card/panel, pad, keyline, asChild."
    >
      <VariantBlock
        label="A panel holding content"
        caption="flat notched surface + hairline keyline + card elevation"
      >
        <NotchPanel className="max-w-sm">
          <h3 className="font-display text-lg font-semibold text-foreground">Workspace memory</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Every source, expert, and report your team has captured — one reasoning graph the agents
            query.
          </p>
        </NotchPanel>
      </VariantBlock>

      <VariantBlock
        label="Elevation — none · soft · card · panel"
        caption="clip-safe drop-shadow depth"
      >
        <div className="flex flex-wrap items-start gap-4">
          {(["none", "soft", "card", "panel"] as const).map((e) => (
            <NotchPanel key={e} elev={e} pad="sm" notch="b" className="w-32">
              <span className="text-xs font-medium text-muted-foreground">elev-{e}</span>
            </NotchPanel>
          ))}
        </div>
      </VariantBlock>

      <VariantBlock
        label="Composes the set — a notched card with a form"
        caption="panel + input + keycap"
      >
        <NotchPanel
          notch="l"
          elev="panel"
          className="max-w-sm"
          style={{ "--primary": "238 79% 64%" } as CSSProperties}
        >
          <h3 className="font-display text-base font-semibold text-foreground">
            Invite a teammate
          </h3>
          <p className="mt-1 mb-3 text-sm text-muted-foreground">They'll join this workspace.</p>
          <div className="flex items-center gap-2">
            <NotchedInput notch="b" placeholder="name@team.com" />
            <KeycapButton notch="b" variant="primary">
              Invite
            </KeycapButton>
          </div>
        </NotchPanel>
      </VariantBlock>
    </Section>
  );
}
